import React, { useEffect, useState, useRef, useCallback } from 'react';
import bugReportService from '../services/bugReportService';
import MathCaptcha from './MathCaptcha';
import { useAuth } from '../hooks/useAuth';
import tenantService from '../services/tenantService';
import userProfileService from '../services/userProfileService';
import { APP_ENV } from '../config';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BugReportModal: React.FC<BugReportModalProps> = ({ isOpen, onClose }) => {
  const { isAuthenticated } = useAuth();
  const [text, setText] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [captchaPayload, setCaptchaPayload] = useState<{ challengeId: string; answer: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [tenantInfo, setTenantInfo] = useState<{ name?: string; subdomain?: string } | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const captchaResetRef = useRef<number>(0);

  const addImageToForm = useCallback((file: File) => {
    setError(null);
    setImages(prev => [...(prev || []), file]);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreviews(prev => [...prev, reader.result as string]);
    };
    reader.readAsDataURL(file);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setText('');
    setImages([]);
    setImagePreviews([]);
    setCaptchaPayload(null);
    setError(null);
    setSuccess(false);
    captchaResetRef.current += 1; // Trigger captcha reset
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Fetch tenant info
    const fetchTenantInfo = async () => {
      try {
        const tenant = await tenantService.getCurrentTenant();
        if (tenant) {
          setTenantInfo({ name: tenant.name, subdomain: tenant.subdomain });
        }
      } catch (error) {
        console.error('Error fetching tenant info:', error);
      }
    };

    // Fetch user email if logged in
    const fetchUserEmail = async () => {
      if (isAuthenticated) {
        try {
          const profile = await userProfileService.getUserProfile();
          if (profile?.email) {
            setUserEmail(profile.email);
          }
        } catch (error) {
          console.error('Error fetching user email:', error);
        }
      }
    };

    fetchTenantInfo();
    fetchUserEmail();
  }, [isOpen, isAuthenticated]);

  useEffect(() => {
    if (!isOpen) return;

    const handleGlobalPaste = (e: Event) => {
      const clipboardEvent = e as ClipboardEvent;
      const items = clipboardEvent.clipboardData?.items;
      if (!items) return;

      const target = clipboardEvent.target as HTMLElement;
      const isTextInput = target.tagName === 'TEXTAREA' || target.tagName === 'INPUT';
      
      // Skip if pasting into textarea (it has its own handler)
      if (isTextInput && target.tagName === 'TEXTAREA') {
        return;
      }

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          clipboardEvent.preventDefault();
          const file = item.getAsFile();
          if (file) {
            addImageToForm(file);
          }
        }
      }
    };

    window.addEventListener('paste', handleGlobalPaste as EventListener);
    return () => {
      window.removeEventListener('paste', handleGlobalPaste as EventListener);
    };
  }, [isOpen, addImageToForm]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      newFiles.forEach(file => {
        addImageToForm(file);
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = useCallback((indexToRemove: number) => {
    setImages(prev => prev.filter((_, index) => index !== indexToRemove));
    setImagePreviews(prev => prev.filter((_, index) => index !== indexToRemove));
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          addImageToForm(file);
        }
      }
    }
  }, [addImageToForm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!captchaPayload) {
      setError('Please complete the math verification');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Convert images to base64
      const imagePromises = images.map(file => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64Image = reader.result as string;
            const imageData = base64Image.split(',')[1];
            resolve(imageData);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      const base64Images = await Promise.all(imagePromises);
      const ok = await bugReportService.submitReport(
        text,
        captchaPayload.challengeId,
        captchaPayload.answer,
        base64Images,
        {
          tenant: tenantInfo?.name || tenantInfo?.subdomain || 'Unknown',
          environment: APP_ENV,
          userEmail: userEmail || undefined
        }
      );
      if (ok) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 1200);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to submit');
      // Reset captcha
      captchaResetRef.current += 1;
      setCaptchaPayload(null);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-deck-panel dark:ring-1 dark:ring-white/10 rounded-lg shadow-xl w-full max-w-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Report a bug</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Describe the issue</label>
            <textarea
              className="w-full h-32 p-2 border rounded-md dark:bg-deck-ink dark:border-white/10 dark:text-white"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={handlePaste}
              placeholder="What happened? Include steps to reproduce if possible. You can paste images here."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Images (optional) - Upload multiple or paste from clipboard
            </label>
            <div className="flex flex-col gap-2">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                ref={fileInputRef}
                className="text-sm text-gray-700 dark:text-gray-300"
              />
              {imagePreviews.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="relative">
                      <img
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        className="w-20 h-20 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Security Verification</label>
            <MathCaptcha 
              onVerify={setCaptchaPayload}
              resetKey={captchaResetRef.current}
            />
          </div>

          {error && (
            <div className="text-sm text-red-600">{error}</div>
          )}
          {success && (
            <div className="text-sm text-green-600">Thanks! Your report was sent.</div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md border dark:border-white/10 text-gray-700 dark:text-gray-200"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`px-4 py-2 rounded-md text-white ${submitting ? 'bg-gray-400' : 'bg-[#FF3EB5] hover:bg-[#E635A3]'}`}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BugReportModal;


